import { parseISO } from 'date-fns';
import axios from '@raas/shared/src/utils/axios';
import {
  IReadLegalProvision,
  ILegalProvisionDetails,
  RegulationWrite,
  LegalProvisionStatus,
} from '@raas/shared/src/types/legal-provision';
import { IFileAssociation } from '@raas/shared/src/types/file-association';
import type { AxiosRequestConfig } from '@raas/shared/src/utils/axios';
import { AxiosResponse } from 'axios';

class RegulationsService {
  private endpoints = {
    root: '/legal-provisions',
    all: '/legal-provisions/all',
    allAssigned: '/legal-provisions/all/assigned',
    allLatest: '/legal-provisions/all/latest',
    allAssignedLatest: '/legal-provisions/all/assigned/latest',
  };

  async getAll(
    abortSignal?: AbortSignal,
    trackMetric?: (name: string, average: number, properties?: Record<string, any>) => void
  ): Promise<IReadLegalProvision[]> {
    const startTime = Date.now();
    const config: AxiosRequestConfig = {};
    if (abortSignal) {
      config.signal = abortSignal;
    }
    const result = await axios.get(this.endpoints.all, config);
    if (trackMetric) {
      trackMetric('API_Response_Time', Date.now() - startTime, {
        endpoint: this.endpoints.all,
        method: 'GET',
        success: true,
      });
    }
    return result.data.map(mapToLegalProvision);
  }

  async getAllAssigned(
    abortSignal?: AbortSignal,
    trackMetric?: (name: string, average: number, properties?: Record<string, any>) => void
  ): Promise<IReadLegalProvision[]> {
    const startTime = Date.now();
    const config: AxiosRequestConfig = {};
    if (abortSignal) {
      config.signal = abortSignal;
    }
    const result = await axios.get(this.endpoints.allAssigned, config);
    if (trackMetric) {
      trackMetric('API_Response_Time', Date.now() - startTime, {
        endpoint: this.endpoints.allAssigned,
        method: 'GET',
        success: true,
      });
    }
    return result.data.map(mapToLegalProvision);
  }

  async getAllLatest(
    abortSignal?: AbortSignal,
    trackMetric?: (name: string, average: number, properties?: Record<string, any>) => void
  ): Promise<IReadLegalProvision[]> {
    const startTime = Date.now();
    const config: AxiosRequestConfig = {};
    if (abortSignal) {
      config.signal = abortSignal;
    }
    const result = await axios.get(this.endpoints.allLatest, config);
    if (trackMetric) {
      trackMetric('API_Response_Time', Date.now() - startTime, {
        endpoint: this.endpoints.allLatest,
        method: 'GET',
        success: true,
      });
    }
    return result.data.map(mapToLegalProvision);
  }

  async getLatestAssigned(
    abortSignal?: AbortSignal,
    trackMetric?: (name: string, average: number, properties?: Record<string, any>) => void
  ): Promise<IReadLegalProvision[]> {
    const startTime = Date.now();
    const config: AxiosRequestConfig = {};
    if (abortSignal) {
      config.signal = abortSignal;
    }
    const result = await axios.get(this.endpoints.allAssignedLatest, config);
    if (trackMetric) {
      trackMetric('API_Response_Time', Date.now() - startTime, {
        endpoint: this.endpoints.allAssignedLatest,
        method: 'GET',
        success: true,
      });
    }
    return result.data.map(mapToLegalProvision);
  }

  async get(id: string, abortSignal?: AbortSignal): Promise<IReadLegalProvision> {
    const config: AxiosRequestConfig = {};
    if (abortSignal) {
      config.signal = abortSignal;
    }
    const result = await axios.get(`${this.endpoints.root}/${id}`, config);
    return mapToLegalProvision(result.data);
  }

  async getTree(
    id: string,
    headers?: any,
    abortSignal?: AbortSignal
  ): Promise<ILegalProvisionDetails> {
    const config: AxiosRequestConfig = {};
    if (abortSignal) {
      config.signal = abortSignal;
    }
    if (headers) {
      config.headers = {
        ...axios.defaults.headers.common,
        ...headers,
      };
    }
    const result = await axios.get(`${this.endpoints.root}/${id}/tree`, config);
    return mapToLegalProvisionDetails(result.data);
  }

  async getTreeExport(
    id: string,
    fileType: string,
    translationId: string,
    abortSignal?: AbortSignal
  ): Promise<{ filename: string; blob: Blob }> {
    const config: AxiosRequestConfig = {
      responseType: 'blob',
    };
    if (abortSignal) {
      config.signal = abortSignal;
    }
    const result = await axios.get(`${this.endpoints.root}/${id}/tree/export`, {
      ...config,
      params: { fileType, translationId },
    });
    const blob = new Blob([result.data], { type: result.headers['content-type'] });
    const filename = this.getFilename(result, id);
    return { filename, blob };
  }

  async getItemVersions(id: string, abortSignal?: AbortSignal): Promise<IReadLegalProvision[]> {
    const config: AxiosRequestConfig = {};
    if (abortSignal) {
      config.signal = abortSignal;
    }
    const result = await axios.get(`${this.endpoints.root}/${id}/versions`, config);
    return result.data.map(mapToLegalProvision);
  }

  async getItemAssignedVersions(
    id: string,
    abortSignal?: AbortSignal
  ): Promise<IReadLegalProvision[]> {
    const config: AxiosRequestConfig = {};
    if (abortSignal) {
      config.signal = abortSignal;
    }
    const result = await axios.get(`${this.endpoints.root}/${id}/assigned/versions`, config);
    return result.data.map(mapToLegalProvision);
  }

  async create(postData: RegulationWrite): Promise<string> {
    const result = await axios.post(this.endpoints.root, mapToRequestBody(postData));
    return result.data;
  }

  async edit(postData: RegulationWrite, id: string): Promise<void> {
    return axios.put(`${this.endpoints.root}/${id}`, mapToRequestBody(postData));
  }

  async getForBundlings(abortSignal?: AbortSignal): Promise<IReadLegalProvision[]> {
    const config: AxiosRequestConfig = {};
    if (abortSignal) {
      config.signal = abortSignal;
    }
    const result = await axios.get('/legal-provision-bundling/legal-provisions', config);
    return result.data.map(mapToLegalProvision);
  }

  private getFilename(response: AxiosResponse, id: string): string {
    const contentDispositionHeader = response.headers['content-disposition'];
    const match =
      contentDispositionHeader && contentDispositionHeader.match(/filename=["']?([^"';\n]+)["']?/);
    return match ? match[1] : `${id}_export.pdf`;
  }
}

const mapToLegalProvision = (legalProvision: any): IReadLegalProvision => ({
  id: legalProvision.id,
  entityId: legalProvision.entityId,
  fullName: legalProvision.fullName,
  abbreviation: legalProvision.abbreviation,
  originalVersion: legalProvision.originalVersion,
  validFrom: legalProvision.validFrom,
  validTo: legalProvision.validTo,
  dateOfPublication: legalProvision.dateOfPublication
    ? parseISO(legalProvision.dateOfPublication)
    : null,
  dateOfAdoption: legalProvision.dateOfAdoption ? parseISO(legalProvision.dateOfAdoption) : null,
  publisher: legalProvision.publisher ? legalProvision.publisher : null,
  type: legalProvision.type,
  sourceName: legalProvision.sourceName,
  sourceLink: legalProvision.sourceLink,
  tags: legalProvision.tags,
  versionId: legalProvision.versionId,
  versionName: legalProvision.versionName,
  versionTimestamp: parseISO(legalProvision.versionTimestamp),
  releaseId: legalProvision.releaseId,
  releaseTimestamp: legalProvision.releaseTimestamp
    ? parseISO(legalProvision.releaseTimestamp)
    : null,
  isComplete: legalProvision.isComplete,
  countryFlag: legalProvision.countryFlag,
  changeLog: legalProvision.changeLog,
  notes: legalProvision.notes,
  translations: legalProvision.translations?.map((translation: any) => ({
    translationId: translation.translationId,
    legalProvisionId: translation.legalProvisionId,
    language: translation.language,
    translationType: translation.translationType,
    publisher: translation.publisher,
    sourceName: translation.sourceName,
    sourceLink: translation.sourceLink,
    notes: translation.notes,
    isDefault: translation.isDefault,
    isHidden: translation.isHidden,
  })),
  legalProvisionContent: legalProvision.legalProvisionContent,
  attachments: legalProvision.attachments?.map((attachment: IFileAssociation) => ({
    id: attachment.id,
    attachmentType: attachment.attachmentType,
    fileName: attachment.fileName,
    entityType: attachment.entityType,
    entityId: attachment.entityId,
    fileMetadataId: attachment.fileMetadataId,
  })),
  status: legalProvision.status ? legalProvision.status : LegalProvisionStatus.NONE,
  summary: legalProvision.summary,
  validToRaw: legalProvision.validToRaw,
  validFromRaw: legalProvision.validFromRaw,
  dateOfPublicationRaw: legalProvision.dateOfPublicationRaw,
  summaryOfChanges: legalProvision.summaryOfChanges,
});

const mapToLegalProvisionDetails = (legalProvision: any): ILegalProvisionDetails => ({
  ...mapToLegalProvision(legalProvision),
  rootElement: legalProvision.rootElement,
});

const mapToRequestBody = (legalProvision: RegulationWrite) => ({
  id: legalProvision.id,
  fullName: legalProvision.fullName,
  abbreviation: legalProvision.abbreviation,
  originalVersion: legalProvision.originalVersion,
  validFrom: legalProvision.validFrom,
  validTo: legalProvision.validTo,
  dateOfPublication: legalProvision.dateOfPublication,
  dateOfAdoption: legalProvision.dateOfAdoption,
  publisher: legalProvision.publisher ? legalProvision.publisher : null,
  type: legalProvision.type,
  sourceName: legalProvision.sourceName,
  sourceLink: legalProvision.sourceLink,
  tagIds: legalProvision.tagIds,
  versionName: legalProvision.versionName,
  releaseId: legalProvision.releaseId,
  isComplete: true,
  countryFlag: legalProvision.countryFlag,
  notes: legalProvision.notes,
  legalProvisionContent: legalProvision.legalProvisionContent,
  language: legalProvision.language,
  translationType: legalProvision.translationType,
  translationId: legalProvision.translationId,
  status:
    legalProvision.status && legalProvision.status !== LegalProvisionStatus.NONE
      ? legalProvision.status
      : undefined,
  summary: legalProvision.summary,
  validToRaw: legalProvision.validToRaw,
  validFromRaw: legalProvision.validFromRaw,
  dateOfPublicationRaw: legalProvision.dateOfPublicationRaw,
  summaryOfChanges: legalProvision.summaryOfChanges,
});

const regulationsService = new RegulationsService();
export default regulationsService;
